import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type TariffsTracker = {
  monthChanged: (params: { month: string }) => void;

  bankDetailViewed: (params: {
    bankId: string;
    sharePct?: number | null;
  }) => void;

  ownerDetailViewed: (params: {
    companyId: string;
    sharePct?: number | null;
  }) => void;

  consolidateRowClicked: (params: {
    companyId?: string | null;
    institutionId?: string | null;
  }) => void;

  breakdownToggled: (params: {
    breakdownType: 'bank' | 'company';
    isVisible: boolean;
  }) => void;

  filterApplied: (params: {
    filterType: 'institution' | 'owner';
    selectedCount: number;
  }) => void;
};

export const useTariffsTracking = (): TariffsTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'tariffs',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<TariffsTracker>(() => ({
    monthChanged: (params) => track('tariffs_month_changed', params),
    bankDetailViewed: (params) => track('tariffs_bank_detail_viewed', params),
    ownerDetailViewed: (params) => track('tariffs_owner_detail_viewed', params),
    consolidateRowClicked: (params) => track('tariffs_consolidate_row_clicked', params),
    breakdownToggled: (params) => track('tariffs_breakdown_toggled', params),
    filterApplied: (params) => track('tariffs_filter_applied', params),
  }), [track]);
};
